import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageHistoryEntry } from 'odq-toolbox';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '~env';
import { PortalMessage, PortalReplyParams } from '../store/portal-messages.model';

@Injectable({ providedIn: 'root' })
export class PortalMessagesService {
  private readonly odqApi = environment.generalPublicOdqApi;
  private readonly farpApi = environment.generalPublicFarpApi;

  constructor(private http: HttpClient) {}

  /**
   * Load all messages for the authenticated user from both ODQ and FarpODQ backends.
   * A 'domain' tag is added client-side so we know which API to call for mutations.
   * Each backend call is fail-safe: a failure on one side does not suppress the other.
   * When both APIs resolve to the same host (local dev), we deduplicate by message id.
   */
  getMyMessages(includeArchived = false): Observable<PortalMessage[]> {
    const params = { includeArchived: String(includeArchived), includeExpired: 'false' };

    const odq$ = this.http
      .get<PortalMessage[]>(`${this.odqApi}/PortalMessages/GetMyMessages`, { params })
      .pipe(
        map(msgs => msgs.map(m => ({ ...m, domain: 'odq' }))),
        catchError(() => of<PortalMessage[]>([]))
      );

    // Skip the farp call entirely when it resolves to the same host as odq (avoids
    // duplicate data and a redundant network request in local-dev setups).
    const farp$: Observable<PortalMessage[]> = this.odqApi === this.farpApi
      ? of([])
      : this.http
          .get<PortalMessage[]>(`${this.farpApi}/PortalMessages/GetMyMessages`, { params })
          .pipe(
            map(msgs => msgs.map(m => ({ ...m, domain: 'farp' }))),
            catchError(() => of<PortalMessage[]>([]))
          );

    return forkJoin([odq$, farp$]).pipe(
      map(([odqMsgs, farpMsgs]) => {
        const seen = new Set<string>();
        return [...odqMsgs, ...farpMsgs]
          .filter(m => { if (seen.has(m.id)) return false; seen.add(m.id); return true; })
          .sort((a, b) => new Date(b.sentDate).getTime() - new Date(a.sentDate).getTime());
      })
    );
  }

  markAsRead(messageId: string, domain: string): Observable<PortalMessage> {
    const apiUrl = this.getApiUrl(domain);
    return this.http.post<PortalMessage>(`${apiUrl}/PortalMessages/MarkAsRead`, { messageId });
  }

  replyToMessage(params: PortalReplyParams): Observable<PortalMessage> {
    const apiUrl = this.getApiUrl(params.domain);
    return this.http.post<PortalMessage>(`${apiUrl}/PortalMessages/ReplyToMessage`, {
      parentMessageId: params.parentMessageId,
      body: params.body,
      subject: params.subject
    });
  }

  getMessageHistory(messageId: string, domain: string): Observable<MessageHistoryEntry[]> {
    const apiUrl = this.getApiUrl(domain);
    return this.http.get<MessageHistoryEntry[]>(`${apiUrl}/PortalMessages/GetMessageHistory`, {
      params: { messageId }
    });
  }

  private getApiUrl(domain: string): string {
    return domain === 'farp' ? this.farpApi : this.odqApi;
  }
}
